import React, { useState, useEffect } from 'react';
import '../index.css';
import './editTable2.css'; // Deine CSS-Datei
import {
  searchRecordInAirTable,
  updateRecordInAirTable,
  createRecordInAirTable
} from '../functions.js/airTableDB';

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newUser, setNewUser] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const fetchedUsers = await searchRecordInAirTable('users');
      setUsers(fetchedUsers || []);
      setIsLoading(false);
    };

    fetchData();
  }, []);

  if (isLoading) {
    return <p>Lade Daten...</p>;
  }

  return (
    <div className="users-container modernContainer">
      <h2>Benutzer</h2>
      <UsersTable
        users={users}
        setUsers={setUsers}
        newUser={newUser}
        setNewUser={setNewUser}
      />
    </div>
  );
};

const UsersTable = ({ users, setUsers, newUser, setNewUser }) => {
  // Zustand für die Bearbeitung der Benutzer
  const [editedUsers, setEditedUsers] = useState({});
  const [originalUsers, setOriginalUsers] = useState({});

  // Initialisiert den Zustand beim ersten Laden / sobald "users" sich ändert
  useEffect(() => {
    const edits = {};
    const originals = {};

    users.forEach(user => {
      edits[user.id] = {
        username: user.fields?.username || '',
        email: user.fields?.email || '',
        password: user.fields?.password || ''
      };
      originals[user.id] = { ...edits[user.id] };
    });

    setEditedUsers(edits);
    setOriginalUsers(originals);
  }, [users]);

  // Lokales Eingabe-Event
  const handleInputChange = (userId, field, value) => {
    setEditedUsers(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        [field]: value
      }
    }));
  };

  // Prüft, ob sich Änderungen gegenüber dem Original ergeben haben
  const hasChanges = (userId) => {
    const original = originalUsers[userId] || {};
    const current = editedUsers[userId] || {};
    return (
      original.username !== current.username ||
      original.email !== current.email ||
      original.password !== current.password
    );
  };

  // Validierung des Benutzers (alle Felder müssen gefüllt sein)
  const validateUser = (userData) => {
    return (
      userData.username.trim() !== '' &&
      userData.email.trim() !== '' &&
      userData.password.trim() !== ''
    );
  };

  // Speichert einen bestehenden Benutzer
  const handleSave = async (userId) => {
    if (!hasChanges(userId)) return;

    const current = editedUsers[userId];
    if (!validateUser(current)) {
      alert('Bitte Benutzername, Email und Passwort ausfüllen.');
      return;
    }

    const updatedData = {
      username: current.username,
      email: current.email,
      password: current.password
    };

    // Update in Airtable
    await updateRecordInAirTable(userId, updatedData, 'users');

    // Aktualisiere den lokalen State
    setUsers(prev =>
      prev.map(user =>
        user.id === userId
          ? { ...user, fields: { ...user.fields, ...updatedData } }
          : user
      )
    );
    // Originalwerte anpassen
    setOriginalUsers(prev => ({
      ...prev,
      [userId]: { ...current }
    }));
  };

  // Änderungen an einem Benutzer verwerfen
  const handleDiscard = (userId) => {
    setEditedUsers(prev => ({
      ...prev,
      [userId]: { ...originalUsers[userId] }
    }));
  };

  // Speichern eines neuen Benutzers
  const handleNewSave = async () => {
    if (!newUser || !validateUser(newUser)) {
      alert('Bitte Benutzername, Email und Passwort ausfüllen.');
      return;
    }

    const newData = {
      username: newUser.username,
      email: newUser.email,
      password: newUser.password
    };

    const result = await createRecordInAirTable(newData, 'users');
    if (result) {
      setUsers(prev => [...prev, result]);
      setNewUser(null);
    }
  };

  // Abbrechen beim Erstellen eines neuen Benutzers
  const handleNewCancel = () => {
    setNewUser(null);
  };

  return (
    <>
      <table className="modern-table">
        <thead>
          <tr>
            <th>Benutzername</th>
            <th>Email</th>
            <th>Passwort</th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {/* Bestehende Benutzer */}
          {users.map(user => (
            <tr key={user.id}>
              <td>
                <input
                  type="text"
                  value={editedUsers[user.id]?.username || ''}
                  onChange={(e) => handleInputChange(user.id, 'username', e.target.value)}
                />
              </td>
              <td>
                <input
                  type="email"
                  value={editedUsers[user.id]?.email || ''}
                  onChange={(e) => handleInputChange(user.id, 'email', e.target.value)}
                />
              </td>
              <td>
                <input
                  type="text"
                  value={editedUsers[user.id]?.password || ''}
                  onChange={(e) => handleInputChange(user.id, 'password', e.target.value)}
                />
              </td>
              <td className="actions-td">
                {hasChanges(user.id) && (
                  <>
                    <button
                      className="btn save"
                      onClick={() => handleSave(user.id)}
                    >
                      💾
                    </button>
                    <button
                      className="btn discard"
                      onClick={() => handleDiscard(user.id)}
                    >
                      ✖
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}

          {/* Neuer Benutzer erstellen */}
          {newUser && (
            <tr className="new-row">
              <td>
                <input
                  type="text"
                  placeholder="Benutzername"
                  value={newUser.username}
                  onChange={(e) =>
                    setNewUser({ ...newUser, username: e.target.value })
                  }
                />
              </td>
              <td>
                <input
                  type="email"
                  placeholder="Email"
                  value={newUser.email}
                  onChange={(e) =>
                    setNewUser({ ...newUser, email: e.target.value })
                  }
                />
              </td>
              <td>
                <input
                  type="text"
                  placeholder="Passwort"
                  value={newUser.password}
                  onChange={(e) =>
                    setNewUser({ ...newUser, password: e.target.value })
                  }
                />
              </td>
              <td className="actions-td">
                <button className="btn save" onClick={handleNewSave}>
                  💾
                </button>
                <button className="btn discard" onClick={handleNewCancel}>
                  ✖
                </button>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Button zum Anlegen eines neuen Benutzers */}
      {!newUser && (
        <button
          className="btn new-group"
          onClick={() => setNewUser({ username: '', email: '', password: '' })}
        >
          + Neuer Benutzer
        </button>
      )}
    </>
  );
};

export default UsersPage;